import { html, render, type TemplateResult } from 'lit-html';
import rowStyles from './pk-report-kpi-row.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

/**
 * `<pk-report-kpi-row>` — horizontal strip of KPI stat cards. v0.7
 *
 * Container element. Drop `<pk-report-kpi>` children inside; the row
 * arranges them in an equal-width flex row that wraps on narrow viewports.
 *
 * ```html
 * <pk-report-kpi-row>
 *   <pk-report-kpi label="Total cost" value="$142.30" delta="+12%" delta-direction="up"></pk-report-kpi>
 *   <pk-report-kpi label="Active users" value="47" delta="-3" delta-direction="down"></pk-report-kpi>
 *   <pk-report-kpi label="Sessions" value="312"></pk-report-kpi>
 * </pk-report-kpi-row>
 * ```
 */
/**
 * Compat data shape for the `items` property (CMS-driven imperative API).
 */
export interface PkKpiItem {
    label?:          string;
    value?:          string | number;
    unit?:           string;
    delta?:          string | number | null;
    deltaDirection?: string;
}

export class PkReportKpiRow extends HTMLElement {
    static readonly tagName = 'pk-report-kpi-row';

    private readonly _root: ShadowRoot;
    private _items: PkKpiItem[] = [];

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._upgradeProperty('items');
        this._doRender();
    }

    // ── Compat data API ────────────────────────────────────────────────────────

    /**
     * Imperative data setter (v0.8 compat). Builds `<pk-report-kpi>` children
     * from a plain array so CMS Razor pages can drive the row via `el.items = [...]`.
     * Backward-compatible with hand-authored `<pk-report-kpi>` markup.
     */
    set items(value: PkKpiItem[] | null | undefined) {
        this._items = Array.isArray(value) ? value : [];
        this._rebuildItems();
    }

    get items(): PkKpiItem[] {
        return this._items;
    }

    private _rebuildItems(): void {
        // Remove only the children this setter manages.
        this.querySelectorAll('pk-report-kpi').forEach(el => el.remove());
        for (const item of this._items) {
            const kpi = document.createElement('pk-report-kpi');
            kpi.setAttribute('label', item.label ?? '');
            const value = item.unit != null
                ? `${item.value ?? ''}${item.unit}`
                : String(item.value ?? '');
            kpi.setAttribute('value', value);
            if (item.delta != null) kpi.setAttribute('delta', String(item.delta));
            if (item.deltaDirection) kpi.setAttribute('delta-direction', item.deltaDirection);
            this.appendChild(kpi);
        }
        // Plain <slot> projects light children automatically; no manual re-render.
    }

    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    private _template(): TemplateResult {
        return html`
            <style>${sharedTokens}${rowStyles}</style>
            <div class="kpi-row">
                <slot></slot>
            </div>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportKpiRow.tagName)) {
    customElements.define(PkReportKpiRow.tagName, PkReportKpiRow);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-kpi>` — individual KPI stat card within a `<pk-report-kpi-row>`. v0.7
 *
 * Attributes:
 *   `label`           — small uppercase label (required).
 *   `value`           — display value, large (required).
 *   `delta`           — optional delta string, e.g. "+12%" or "-3".
 *   `delta-direction` — `up` | `down` | `neutral` (optional; controls color).
 *                       Semantic meaning of up/down is consumer-defined.
 */
export class PkReportKpi extends HTMLElement {
    static readonly tagName = 'pk-report-kpi';

    static get observedAttributes(): string[] {
        return ['label', 'value', 'delta', 'delta-direction'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._doRender();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    private _deltaDirection(): 'up' | 'down' | 'neutral' {
        const v = this.getAttribute('delta-direction');
        if (v === 'up' || v === 'down') return v;
        return 'neutral';
    }

    private _template(): TemplateResult {
        const label     = this.getAttribute('label') ?? '';
        const value     = this.getAttribute('value') ?? '';
        const delta     = this.getAttribute('delta');
        const direction = this._deltaDirection();

        return html`
            <style>
                ${sharedTokens}
                :host {
                    display: block;
                    font-family: var(--pk-font-family, 'Segoe UI', sans-serif);
                }
                .kpi-card {
                    padding: var(--pk-report-panel-pad, 1rem);
                    border: 1px solid var(--pk-color-border, #ECE8DA);
                    border-radius: var(--pk-radius-md, 8px);
                    background: var(--pk-color-bg-elevated, #fff);
                    box-shadow: var(--pk-shadow-card, 0 8px 22px rgba(24,27,34,0.10));
                    display: flex;
                    flex-direction: column;
                    gap: var(--pk-space-2, 0.4rem);
                }
                .kpi-label {
                    font-size: var(--pk-font-size-xs, 0.72rem);
                    font-weight: var(--pk-font-weight-medium, 500);
                    letter-spacing: var(--pk-letter-spacing-eyebrow, 0.18em);
                    text-transform: uppercase;
                    color: var(--pk-color-text-muted, rgba(24,27,34,0.65));
                }
                .kpi-value {
                    font-size: clamp(1.4rem, 2vw, 1.85rem);
                    font-weight: var(--pk-font-weight-display, 800);
                    color: var(--pk-color-text, #181B22);
                    line-height: var(--pk-line-height-tight, 1.15);
                }
                .kpi-delta {
                    font-size: var(--pk-font-size-sm, 0.85rem);
                    font-weight: var(--pk-font-weight-medium, 500);
                }
                .kpi-delta[data-dir="up"]      { color: var(--pk-report-delta-up,      #1a7f4e); }
                .kpi-delta[data-dir="down"]    { color: var(--pk-report-delta-down,    #a23131); }
                .kpi-delta[data-dir="neutral"] { color: var(--pk-report-delta-neutral, rgba(24,27,34,0.65)); }
            </style>
            <div class="kpi-card">
                <div class="kpi-label">${label}</div>
                <div class="kpi-value">${value}</div>
                ${delta ? html`<div class="kpi-delta" data-dir=${direction}>${delta}</div>` : ''}
            </div>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportKpi.tagName)) {
    customElements.define(PkReportKpi.tagName, PkReportKpi);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-kpi-row': PkReportKpiRow;
        'pk-report-kpi':     PkReportKpi;
    }
}
